/* ==========================================================================
   LP landing-page motion layer.

   Pairs with assets/lp-anim.css. Loaded once from layout/theme.liquid on the
   lp / lp-blank templates, so unlike lp-pdp.js it does not need a heavy
   re-entry guard - but it keeps a light one anyway in case a section ever
   starts emitting it too.

   Everything here is additive: if this file fails to load, the page looks and
   behaves exactly as it did before, because the CSS that hides things is
   gated on the .lp-anim class that only this file adds.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__lpAnimInit) return;
  window.__lpAnimInit = true;

  // Honour the OS-level setting. Bailing out before adding the class means the
  // "start at opacity 0" rules never apply, so content is simply always there.
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  document.documentElement.classList.add('lp-anim');

  /* ------------------------------------------------------------------
     Count the stat numbers up when their card enters the viewport.
     The label is free text ("89%", "1,200+", "2.4x"), so pull out the first
     number, animate that, and put whatever was around it back afterwards.
     ------------------------------------------------------------------ */
  function countUp(el) {
    if (el.dataset.lpCounted) return;

    var raw = el.textContent;
    var match = raw.match(/-?\d+(?:\.\d+)?/);
    if (!match) return;

    el.dataset.lpCounted = '1';

    var target = parseFloat(match[0]);
    var decimals = (match[0].split('.')[1] || '').length;
    var prefix = raw.slice(0, match.index);
    var suffix = raw.slice(match.index + match[0].length);
    var duration = 1100;
    var start = null;

    function frame(now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / duration, 1);
      // ease-out cubic: fast off the mark, settles gently on the real figure
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (t < 1) requestAnimationFrame(frame);
      else el.textContent = raw;
    }

    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------
     One observer for everything.

     Reveals are one-shot: once an element has appeared it is unobserved, so
     it never replays on the way back up and the observer empties itself out.

     The review cards are the exception. Their star is an infinite animation,
     and three of those ticking away while the card is off screen is wasted
     work, so those stay observed and the class is toggled both ways.
     ------------------------------------------------------------------ */
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var el = entry.target;
      var isCard = el.classList.contains('lp-revcard');

      if (!entry.isIntersecting) {
        if (isCard) el.classList.remove('is-in');
        return;
      }

      el.classList.add('is-in');
      if (!isCard) observer.unobserve(el);

      var num = el.querySelector('.lp-study__num');
      if (num) countUp(num);
    });
  }, {
    threshold: 0.15,
    // Fire a little before the element is properly on screen, so the movement
    // is finishing as the user arrives rather than starting.
    rootMargin: '0px 0px -12% 0px'
  });

  document.querySelectorAll('.lp-jr__stage, .lp-study__stat, .lp-revcard, .lp-swap__row')
    .forEach(function (el) { observer.observe(el); });

  /* ------------------------------------------------------------------
     Timeline rail fill.

     Sets --lp-jr-progress (0..1) on each .lp-jr from how far the page has
     scrolled through it, measured against a line 62% down the viewport.

     The What To Expect timeline lives inside a tab panel, so a hidden .lp-jr
     has a zero-size rect and would otherwise be pinned at 1. offsetParent is
     null for anything display:none'd, which is the cheap way to skip those.
     ------------------------------------------------------------------ */
  var rails = document.querySelectorAll('.lp-jr');
  if (!rails.length) return;

  var queued = false;

  function update() {
    queued = false;
    var line = window.innerHeight * 0.62;

    rails.forEach(function (rail) {
      if (!rail.offsetParent) return;

      var rect = rail.getBoundingClientRect();
      if (!rect.height) return;

      var progress = (line - rect.top) / rect.height;
      progress = Math.max(0, Math.min(1, progress));
      rail.style.setProperty('--lp-jr-progress', progress.toFixed(3));
    });
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  // Switching tabs reveals a timeline that was display:none and therefore
  // skipped above. Capture phase so this runs whatever the tab handler does.
  document.addEventListener('click', function () {
    setTimeout(update, 80);
  }, true);

  update();
})();
